/**
 * Tags Dropdown Field Type
 *
 * Provides configuration and renderers for single-select dropdown fields
 * that display their value as a bordered tag:
 * - Read-only column renderer (displays value as a bordered Tag)
 * - Editable column renderer (Autocomplete dropdown or API-backed ComboBox)
 * - Form field renderer (uses dropdown form field)
 *
 * Used for single-select AllowedValue custom fields where tag display is preferred.
 * Uses the same Tag styled component as the 'tags' field type for visual consistency.
 * Supports both static options AND lookup registry APIs.
 */

import React, { useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { ApiResponse } from 'apisauce';
import type { Response } from 'types';
import type { ComboBoxOption } from 'components/inputs/types';
import { Autocomplete, ComboBox } from 'components';
import { TableTooltip } from '../../../TableTooltip';
import { useEditableContextOptional } from '../../../OptimizedTableProvider';
import { SharedEditableCell } from '../SharedEditableCell';
import { clearEditOnSuccess } from '../inlineEditUtils';
import { ClickAwayListener } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { t } from '@lingui/macro';
import { CellContainer, TagsContainer, Tag, InvalidTag } from '../../../tableStyles';
import type { FieldTypeConfig } from '../../../../components/types';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../../columnModel';
import type { CustomFieldValue } from '../../../customFields';
import { M } from '../../columnWidths';

// Import shared cell value utility
import { getCellValue } from '../../cellValue';

/** Option with optional parentId in customProps (for NestedAllowedValue) */
type OptionWithParent = {
  id: string | number;
  label: string;
  customProps?: { parentId?: number | null };
};

/**
 * Get parentId from option's customProps (set for NestedAllowedValue)
 */
const getOptionParentId = (opt: OptionWithParent): number | null | undefined =>
  opt.customProps?.parentId;

/**
 * Filter options for hierarchical (NestedAllowedValue) fields: show only top-level options
 * and children of the currently selected parent. Child options are hidden until a parent is selected.
 * Note: Backend returns 0 for "no parent", so we treat 0, null, and undefined as parent-level.
 * If no hierarchy exists (no customProps or all parentId are undefined), return all options.
 */
const filterOptionsForNested = (
  options: OptionWithParent[] | undefined,
  selectedOption: OptionWithParent | null | undefined
): OptionWithParent[] => {
  if (!options || options.length === 0) return [];

  // Check if this is actually a hierarchical field
  // If no options have customProps.parentId defined at all, just return all options
  const hasHierarchy = options.some(opt => opt.customProps?.parentId !== undefined);
  if (!hasHierarchy) {
    return options;
  }

  const selectedId = selectedOption?.id;
  const filtered = options.filter(opt => {
    const parentId = getOptionParentId(opt);
    // Treat 0, null, and undefined as parent-level (no parent)
    const isTopLevel = parentId === undefined || parentId === null || parentId === 0;
    if (isTopLevel) return true;
    return selectedId !== undefined && selectedId !== null && parentId === selectedId;
  });

  // If filtering resulted in no options, return all options as fallback
  // This handles edge cases where the data might be malformed
  return filtered.length > 0 ? filtered : options;
};

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Check if a value is valid (exists in the allowed options)
 * Returns true if value is empty or found in options, false if value exists but not in options
 */
const isValueValid = (
  cellValue: string,
  options?: Array<{ id: string | number; label: string }>
): boolean => {
  // Empty values are always valid
  if (!cellValue) return true;
  // If no options provided, we can't validate - assume valid
  if (!options || options.length === 0) return true;
  // Check if value exists in options (match by label or id)
  return options.some(opt => opt.label === cellValue || String(opt.id) === cellValue);
};

// ============================================================================
// Lookup Registry Support
// ============================================================================

// Import getLookupApi lazily to avoid circular dependencies
let getLookupApiFn: ((columnId: string) => unknown) | null = null;

/**
 * Set the getLookupApi function (called from initDependencies.ts)
 */
export const setGetLookupApiForTagsDropdown = (fn: (columnId: string) => unknown): void => {
  getLookupApiFn = fn;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper to get display value from cell context for tags dropdown
 * Uses getCellValue (respects accessorFn) and handles array/object formats
 */
const getCellDisplayValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): string => {
  const value = getCellValue(context, context.column.id);

  if (value === undefined || value === null) {
    return '';
  }

  // Handle array values (shouldn't happen for single-select, but handle gracefully)
  if (Array.isArray(value)) {
    return value.map(v => (typeof v === 'object' && v !== null ? v.name || v.label : v)).join(', ');
  }

  // Handle object with 'name' or 'label' property
  if (typeof value === 'object') {
    return String(
      (value as { name?: string; label?: string }).name || (value as { label?: string }).label || ''
    );
  }

  return String(value);
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Tags dropdown read-only renderer - displays value as a bordered Tag
 * Uses col.id as the data field
 * Includes internal Tooltip for consistent hover behavior
 * Supports placeholder display when no value is present
 * Shows InvalidTag styling if value doesn't exist in allowed options
 */
export const tagsDropdownReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const cellValue = getCellDisplayValue(cellContext);

    if (!cellValue) {
      // Show placeholder if defined, otherwise show dash
      if (col.placeholder) {
        return (
          <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
            {col.placeholder}
          </CellContainer>
        );
      }
      return <CellContainer>-</CellContainer>;
    }

    // Check if value is valid (exists in allowed options)
    const isValid = isValueValid(cellValue, col.options);
    const TagComponent = isValid ? Tag : InvalidTag;
    const tooltipText = isValid ? cellValue : `${cellValue} (invalid value)`;

    return (
      <TableTooltip title={tooltipText}>
        <CellContainer>
          <TagsContainer>
            <TagComponent>{cellValue}</TagComponent>
          </TagsContainer>
        </CellContainer>
      </TableTooltip>
    );
  },
  tooltipRender: (cell: CellContext<T, unknown>) => getCellDisplayValue(cell),
});

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Tags dropdown editable renderer - displays as bordered Tag, edits via Autocomplete or ComboBox
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 * Supports both static options AND lookup registry APIs
 */
export const tagsDropdownEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const resolvedApi = col.api || (getLookupApiFn ? getLookupApiFn(col.id) : null);
  const hasStaticOptions = col.options && col.options.length > 0;
  const hasNoDataSource = !resolvedApi && !hasStaticOptions;
  const editableProp = col.editable;

  // If no data source available, render as read-only with tag display
  if (hasNoDataSource) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const cellValue = getCellDisplayValue(context);
        if (!cellValue) {
          // Show placeholder if defined, otherwise show empty space
          if (col.placeholder) {
            return (
              <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
                {col.placeholder}
              </CellContainer>
            );
          }
          return <CellContainer> </CellContainer>;
        }
        // No options to validate against, show as potentially invalid
        return (
          <TableTooltip title={`${cellValue} (unable to validate)`}>
            <CellContainer>
              <TagsContainer>
                <InvalidTag>{cellValue}</InvalidTag>
              </TagsContainer>
            </CellContainer>
          </TableTooltip>
        );
      },
      meta: {
        editable: false,
      },
    };
  }

  // If we have an API, use the API-backed ComboBox
  if (resolvedApi) {
    // Cast to the ComboBox API type
    const apiConfig = resolvedApi as {
      url:
        | ((params: {
            query?: URLSearchParams;
            vars?: string[];
          }) => Promise<ApiResponse<unknown, unknown>>)
        | null;
      key: string;
      vars?: string[];
      revalidate?: boolean;
      search?: boolean;
      responseTransform: (data: unknown) => Array<{ id: number | string; label: string }>;
    };

    return {
      cell: (context: CellContext<T, unknown>) => {
        const cellValue = getCellDisplayValue(context);

        // Evaluate editability for this specific row
        const isRowEditable =
          typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

        // For API-backed dropdowns, we validate against col.options if available
        // (options may be pre-populated from custom field definitions)
        const isValid = isValueValid(cellValue, col.options);
        const TagComponent = isValid ? Tag : InvalidTag;
        const tooltipText = cellValue ? (isValid ? cellValue : `${cellValue} (invalid value)`) : '';

        // Read-only display - single tag with tooltip, shows invalid styling if needed
        const readOnlyDisplay = (
          <TableTooltip title={tooltipText}>
            <CellContainer>
              {cellValue ? (
                <TagsContainer>
                  <TagComponent>{cellValue}</TagComponent>
                </TagsContainer>
              ) : (
                ' '
              )}
            </CellContainer>
          </TableTooltip>
        );

        // If not editable for this row, show placeholder if no value and placeholder is defined
        if (!isRowEditable) {
          const isEmpty = !cellValue || (typeof cellValue === 'string' && cellValue.trim() === '');

          if (isEmpty && col.placeholder) {
            return (
              <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
                {col.placeholder}
              </CellContainer>
            );
          }
          return readOnlyDisplay;
        }

        return (
          <TagsDropdownApiCell
            context={context}
            col={col}
            cellValue={cellValue}
            tooltipText={tooltipText}
            TagComponent={TagComponent}
            apiConfig={apiConfig}
          />
        );
      },
      meta: {
        editable: true,
      },
    };
  }

  // Static options path - use Autocomplete with static options
  return {
    cell: (context: CellContext<T, unknown>) => {
      const cellValue = getCellDisplayValue(context);

      // Evaluate editability for this specific row
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      // Check if value is valid (exists in allowed options)
      const isValid = isValueValid(cellValue, col.options);
      const TagComponent = isValid ? Tag : InvalidTag;
      const tooltipText = cellValue ? (isValid ? cellValue : `${cellValue} (invalid value)`) : '';

      // Read-only display - single tag with tooltip, shows invalid styling if needed
      const readOnlyDisplay = (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            {cellValue ? (
              <TagsContainer>
                <TagComponent>{cellValue}</TagComponent>
              </TagsContainer>
            ) : (
              ' '
            )}
          </CellContainer>
        </TableTooltip>
      );

      // If not editable for this row, show placeholder if no value and placeholder is defined
      if (!isRowEditable) {
        const isEmpty = !cellValue || (typeof cellValue === 'string' && cellValue.trim() === '');

        if (isEmpty && col.placeholder) {
          return (
            <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
              {col.placeholder}
            </CellContainer>
          );
        }
        return readOnlyDisplay;
      }

      // Find current value in options
      const currentOption = col.options?.find(
        opt => opt.label === cellValue || String(opt.id) === cellValue
      );

      // CROSS-FIELD DEPENDENCY: Filter based on parent custom field's selected value
      let baseOptions = col.options;
      if ((col as { parentCustomFieldId?: number }).parentCustomFieldId) {
        const parentCustomFieldId = (col as { parentCustomFieldId?: number }).parentCustomFieldId;

        // Get the parent field's value from the row
        const parentFieldValue = (
          context.row.original as { customFields?: CustomFieldValue[] }
        ).customFields?.find(cf => cf.customFieldId === parentCustomFieldId);

        const parentSelectedId = parentFieldValue?.itemCustomFieldAllowedValueId;

        if (parentSelectedId) {
          // Parent has a value selected - filter to show only children of that value
          baseOptions = baseOptions?.filter(
            (opt: ComboBoxOption) => opt.customProps?.parentId === parentSelectedId
          );
        } else {
          // Parent not selected - show no options
          baseOptions = [];
        }
      }

      // WITHIN-FIELD HIERARCHY: For hierarchical fields (isNestedDropdown flag),
      // show only top-level options and children of selected parent
      const isNested = (col as { isNestedDropdown?: boolean }).isNestedDropdown === true;
      if (isNested && baseOptions) {
        baseOptions = filterOptionsForNested(
          baseOptions as OptionWithParent[] | undefined,
          currentOption as OptionWithParent | null | undefined
        );
      }

      const clearOption = { id: '__clear__', label: t`Clear` };
      const optionsWithClear =
        cellValue && !col.required ? [clearOption, ...(baseOptions || [])] : baseOptions;

      return (
        <TagsDropdownStaticCell
          context={context}
          col={col}
          cellValue={cellValue}
          readOnlyDisplay={readOnlyDisplay}
          currentOption={currentOption ?? null}
          optionsWithClear={optionsWithClear}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

const TagsDropdownApiCell = <T extends { id: number | string }>({
  context,
  col,
  cellValue,
  tooltipText,
  TagComponent,
  apiConfig,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  cellValue: string;
  tooltipText: string;
  TagComponent: React.ElementType;
  apiConfig: {
    url:
      | ((params: {
          query?: URLSearchParams;
          vars?: string[];
        }) => Promise<ApiResponse<unknown, unknown>>)
      | null;
    key: string;
    vars?: string[];
    responseTransform: (data: unknown) => Array<{ id: number | string; label: string }>;
  };
}) => {
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);
  const clearEdit = useCallback(
    () => setEditableState({ rowIndex: null, columnId: null }),
    [setEditableState]
  );
  const editableState = editableCtx?.editableState ?? null;
  const isEditing =
    context.row.index === (editableState?.rowIndex ?? -1) &&
    context.column.id === (editableState?.columnId ?? '');
  const handleClick = useCallback(() => {
    if (!isEditing && context.row.original.id !== -1) {
      setEditableState({ rowIndex: context.row.index, columnId: context.column.id });
    }
  }, [isEditing, context, setEditableState]);

  const apiConfigWithClear = {
    ...apiConfig,
    responseTransform: (data: unknown) => {
      const apiOptions = apiConfig.responseTransform(data);
      if (cellValue && !col.required) {
        const clearOption = { id: '__clear__', label: t`Clear` };
        return [clearOption, ...apiOptions];
      }
      return apiOptions;
    },
  };

  return (
    <CellContainer onClick={handleClick} style={{ cursor: 'pointer' }}>
      {isEditing ? (
        <ClickAwayListener onClickAway={clearEdit}>
          <div style={{ width: '100%', minWidth: 150 }}>
            <ComboBox
              id={`${col.id}-edit`}
              api={apiConfigWithClear}
              open
              value={cellValue}
              onChange={(_, selectedValue) => {
                if (!col.onUpdate) {
                  clearEdit();
                  return;
                }
                let result: void | Promise<void>;
                if (
                  selectedValue &&
                  typeof selectedValue !== 'string' &&
                  !Array.isArray(selectedValue) &&
                  selectedValue.id
                ) {
                  if (selectedValue.id === '__clear__') {
                    result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
                  } else {
                    const updateValue = {
                      id: selectedValue.id,
                      name: selectedValue.label || '',
                    };
                    result = col.onUpdate(context.row.original, {
                      [col.id]: updateValue,
                    } as Partial<T>);
                  }
                } else {
                  result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
                }
                clearEditOnSuccess(result, clearEdit);
              }}
            />
          </div>
        </ClickAwayListener>
      ) : cellValue ? (
        <TableTooltip title={tooltipText}>
          <TagsContainer>
            <TagComponent>{cellValue}</TagComponent>
          </TagsContainer>
        </TableTooltip>
      ) : (
        <span> </span>
      )}
    </CellContainer>
  );
};

const TagsDropdownStaticCell = <T extends { id: number | string }>({
  context,
  col,
  cellValue,
  readOnlyDisplay,
  currentOption,
  optionsWithClear,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  cellValue: string;
  readOnlyDisplay: React.ReactNode;
  currentOption: ComboBoxOption | null;
  optionsWithClear: ComboBoxOption[] | undefined;
}) => {
  const editableCtx = useEditableContextOptional();
  const clearEdit = useCallback(
    () => editableCtx?.setEditableState?.({ rowIndex: null, columnId: null }),
    [editableCtx?.setEditableState]
  );

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true }}
      tooltipTitle={cellValue}
      readModeEl={readOnlyDisplay}
    >
      <Autocomplete
        id={`${col.id}-edit`}
        options={optionsWithClear ?? []}
        variant="standard"
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={currentOption ?? null}
        onChange={(_, newValue) => {
          if (!col.onUpdate) {
            clearEdit();
            return;
          }
          let result: void | Promise<void>;
          if (newValue?.id === '__clear__') {
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
            const updateValue = { id: newValue.id, name: newValue.label || '' };
            result = col.onUpdate(context.row.original, { [col.id]: updateValue } as Partial<T>);
          } else {
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          }
          clearEditOnSuccess(result, clearEdit);
        }}
        onBlur={clearEdit}
        onKeyDown={e => {
          if (e.key === 'Enter') clearEdit();
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.id === '__clear__') return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          if (option?.id === '__clear__') return '';
          return option?.label ?? '';
        }}
        autoFocus
        openOnFocus
        sx={{ minWidth: 150 }}
      />
    </SharedEditableCell>
  );
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Tags dropdown field type configuration
 */
export const tagsDropdownFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...M,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const tagsDropdownReadOnly: ColumnRenderer = tagsDropdownReadOnlyRenderer;

/** Editable renderer with proper typing */
export const tagsDropdownEditable: ColumnRenderer = tagsDropdownEditableRenderer;
